import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

// Hooks
import { useMakes, useModelsByMake } from "@/Common/hooks/MakeModels";

// Helpers
import { getWildcardModels } from "@/Common/helpers/Brands";

// Layouts
import AgeOptions from "@/Layouts/components/AgeOptions";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// MUI
import { Grid, Stack, SvgIcon } from "@mui/material";

// Icons
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useValidations } from "@/Common/hooks/Validations";

const mapInitialModel = (properties, addons) => {
	if (!properties && !addons) {
		return {
			make: "",
			model: "",
			modelYears: { rangeType: "RELATIVE", left: null, right: null },
		};
	}

	const { regularExpression: hasExpression = false, applyRetroactively: hasSwitch = false } = addons || {};

	const { make, model, modelYears, regularExpression, applyTiersRetroactively } = properties || {};

	const definition = { make, model, modelYears };

	if (hasExpression) {
		definition["regularExpression"] = regularExpression || "";
	}
	if (hasSwitch) {
		definition["applyTiersRetroactively"] = Boolean(applyTiersRetroactively);
	}

	return definition;
};

export default function MakeModelSelector(props) {
	const {
		index,
		modelKey = "",
		children,
		allowSpecificModels = false,
		deleteTitle = "Remove Model",
		make = "",
		model = "",
		modelYears = { rangeType: "RELATIVE", left: null, right: null },
		makes = [],
		regularExpression = undefined,
		applyTiersRetroactively = undefined,
		modelsAddons = {
			regularExpression: false,
			applyRetroactively: false,
		},
		errors,
		loading = false,
		readOnly = false,
		useRegexModels = false,
		onActionDelete = () => {},
		onActionUpdate = () => {},
	} = props;

	const [actualModel, setActualModel] = useState({
		make: "",
		model: "",
		modelYears: { rangeType: "RELATIVE", left: null, right: null },
		regularExpression: "",
	});

	const location = useSelector((state) => state.location.location);
	const availableMakes = useMakes(location, modelKey, makes);
	const { models, isLoading } = useModelsByMake(actualModel.make);

	const hasError = useCallback((validationKey, rowIndex, fieldName) => {
		return !!errors?.get(validationKey)?.[rowIndex]?.[fieldName];
	}, [errors]);

	const handleUpdateRegex = (value, currentModel) => {
		value = value ? value.toUpperCase() : value;
		const updatedModel = { ...currentModel, regularExpression: value };
		onActionUpdate?.(updatedModel);
	};

	const handleChangeModels = (area, value = null) => {
		const updatedModel = { ...actualModel, [area]: value };

		if (area === "model") {
			updatedModel.modelYears.left = null;
			updatedModel.modelYears.right = null;
		}

		setActualModel(updatedModel);
		onActionUpdate?.(updatedModel);
	};

	const handleChangeYears = (updatedModelYears) => {
		const updatedModel = { ...actualModel, modelYears: updatedModelYears };
		setActualModel(updatedModel);
		onActionUpdate?.(updatedModel);
	};

	const handleUpdateSwitch = (area, value) => {
		const updatedModel = { ...actualModel, [area]: value };
		setActualModel(updatedModel);
		onActionUpdate?.(updatedModel);
	};

	const getSelectedValue = (key, currentModel) => (currentModel?.[key] ? { [key]: currentModel[key] } : null);

	const isFieldInvalid = (fieldName) => {
		const fieldInvalid = hasError("models", index, fieldName);
		const hasMakeOrRegularExpression = !!actualModel?.make || !!actualModel?.regularExpression;
        switch (fieldName) {
			case "make":
			case "regularExpression":
				return fieldInvalid || !hasMakeOrRegularExpression;
			default:
				return fieldInvalid;
		}
	};

	useEffect(() => {
		if (!location) {
			return;
		}

		setActualModel(mapInitialModel({ make, model, modelYears, regularExpression, applyTiersRetroactively }, modelsAddons));

		return () => {
			setActualModel({
				make: "",
				model: "",
				modelYears: { rangeType: "RELATIVE", left: null, right: null },
			});
		};
	}, [location, modelsAddons, make, model, modelYears, regularExpression, applyTiersRetroactively]);

	const isSwitchInPlace = Boolean(modelsAddons.applyRetroactively);

	return (
		<Grid container rowSpacing={2} mb={4} className={"model-tiered-container"}>
			<Grid item xs={12}>
				<Stack direction="row" spacing={6}>
					<Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
						<Stack width={"170px"}>
							<SmartSelector
								showBy={"make"}
								selectBy={"make"}
								options={availableMakes}
								selected={getSelectedValue("make", actualModel)}
								inputProps={{
									placeholder: "Select Make",
								}}
								listBoxProps={{
									sx: { maxHeight: "220px" },
								}}
								popperProps={{
									sx: { minWidth: "190px", maxWidth: "240px" },
								}}
								sx={{ minWidth: "100%" }}
								clearable
								disabled={readOnly}
								error={isFieldInvalid("make")}
								loading={loading}
								onChangeAction={(value) => handleChangeModels("make", value?.make)}
							/>
						</Stack>
						<Stack width={isSwitchInPlace ? "170px" : "300px"}>
							<SmartSelector
								showBy={"model"}
								selectBy={"model"}
								options={getWildcardModels(models, useRegexModels) || []}
								selected={getSelectedValue("model", actualModel)}
								inputProps={{
									placeholder: "Select Model",
								}}
								listBoxProps={{
									sx: { maxHeight: "220px" },
								}}
								popperProps={{
									sx: { minWidth: "300px", maxWidth: "350px" },
								}}
								sx={{ minWidth: "100%" }}
								clearable
								disabled={readOnly}
								error={isFieldInvalid("model")}
								loading={isLoading}
								onChangeAction={(value) => handleChangeModels("model", value?.model)}
							/>
						</Stack>
					</Stack>
					<Stack direction="row" justifyContent="center" alignItems="center" sx={{ marginLeft: "30px !important" }}>
						<AgeOptions
							make={actualModel?.make}
							model={actualModel?.model}
							left={actualModel?.modelYears?.left}
							right={actualModel?.modelYears?.right}
							rangeType={actualModel?.modelYears?.rangeType || "RELATIVE"}
							relativeTitle={"Current Model"}
							useSwitch={allowSpecificModels}
							sxSwitch={{ marginTop: "-33px" }}
							sxLeft={{ width: "100px" }}
							sxRight={{ width: "100px" }}
							clearable={true}
							disabled={readOnly}
							error={isFieldInvalid("modelYears")}
							onActionUpdate={handleChangeYears}
						/>

						{modelsAddons.regularExpression && (
							<Stack sx={{ minWidth: "115px" }}>
								<SimpleTextField
									editable={!readOnly}
									outlined
									text={actualModel?.regularExpression || ""}
									placeholder="Stock Expression"
									error={isFieldInvalid("regularExpression")}
									onActionUpdate={(value) => handleUpdateRegex(value, actualModel)}
									modifier={"toUpperCase"}
								/>
							</Stack>
						)}

						<DoubleCheckingButton
							title={deleteTitle}
							icon={<CloseRoundedIcon />}
							iconHover={<CloseRoundedIcon />}
							endIcon={<SvgIcon className="!text-[16px] ml-[4px]" component={TrashIcon} viewBox={"0 0 14 14"} />}
							confirmationButtonClasses={"!min-w-[130px] hover:!border-gray-blue hover:!bg-gray-blue hover:!shadow-gray-blue"}
							mainButtonClasses={"btn-simple-remove"}
							mainSx={{ marginLeft: "10px" }}
							confirmationSx={{ right: 0, left: "10px" }}
							disabled={readOnly}
							onDeleteAction={() => onActionDelete?.()}
						/>

						{isSwitchInPlace && (
							<Stack sx={{ minWidth: "235px" }}>
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={actualModel?.applyTiersRetroactively}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitch("applyTiersRetroactively", value)}
								/>
							</Stack>
						)}
					</Stack>
				</Stack>
			</Grid>
			<Grid item xs={12}>
				{children}
			</Grid>
		</Grid>
	);
}
MakeModelSelector.propTypes = {
	modelKey: PropTypes.string,
	children: PropTypes.node,
	allowSpecificModels: PropTypes.bool,
	deleteTitle: PropTypes.string,
	make: PropTypes.string,
	model: PropTypes.string,
	modelYears: PropTypes.shape({
		rangeType: PropTypes.string,
		left: PropTypes.number,
		right: PropTypes.number,
	}),
	modelsAddons: PropTypes.shape({
		regularExpression: PropTypes.bool,
		applyRetroactively: PropTypes.bool,
	}),
	makes: PropTypes.array,
	regularExpression: PropTypes.string,
	applyTiersRetroactively: PropTypes.bool,
	errors: PropTypes.object,
	loading: PropTypes.bool, // Previously known as loadingMakes
	readOnly: PropTypes.bool,
	errorRegex: PropTypes.bool,
	useRegexModels: PropTypes.bool,
	onActionDelete: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
